import { skipToken } from '@reduxjs/toolkit/query';
import { useEffect, useMemo, useRef, useState, type SetStateAction } from 'react';
import { toast } from 'sonner';
import { getInitials } from '@/lib';
import {
  useCreateEnrolledStudentsMutation,
  usePatchAcceptLeaveMutation,
  useCreateStudentsAttendanceMutation,
  useDeleteEnrolledStudentMutation,
  useGetEnrolledStudentsQuery,
  useGetRequestLeaveByDateQuery,
  useGetCourseReportQuery,
  useGetCourseMarkingSchemeQuery,
  useGetStudentAttendanceQuery,
  useGetStudentsAttendanceByDateQuery,
  usePatchEnrolledStudentMutation,
  useUpdateStudentsAttendanceMutation,
} from '@/state/course';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { RichTextEditor } from '@/components/rich-text';
import { ReadOnlyContentDisplay } from '@/components/read-only-content-display';
import CourseStudentAnalyticsPanel from './course-student-analytics-panel';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  ChevronLeft,
  ChevronRight,
  Check,
  CircleAlert,
  LayoutGrid,
  List,
  Pencil,
  Plus,
  Search,
  SlidersHorizontal,
  X,
  ArrowLeft,
} from 'lucide-react';
import type { LeaveRequestRecord } from '@/types/api/course.types';
import type { Course } from '@/types/api/course.types';

interface UsersAttendanceContentProps {
  tenant: string;
  courseId: number;
  boardId?: number | null;
  boardName?: string | null;
  canEdit: boolean;
  mode: 'users' | 'attendance';
  courseGradingStructure?: Course['grading_structure'];
}

type AttendanceStatus = 1 | 2 | null;
type LeaveStatus = 1 | 2 | 3;
const EMPTY_ARRAY: never[] = [];

const formatUTCDateISO = (date: Date) => {
  const y = date.getUTCFullYear();
  const m = String(date.getUTCMonth() + 1).padStart(2, '0');
  const d = String(date.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const parseISOAsUTCDate = (dateISO: string) => {
  const [y, m, d] = dateISO.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d));
};

const todayISO = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(
    now.getDate()
  ).padStart(2, '0')}`;
};

const addDaysISO = (dateISO: string, days: number) => {
  const date = parseISOAsUTCDate(dateISO);
  date.setUTCDate(date.getUTCDate() + days);
  return formatUTCDateISO(date);
};

const formatDisplayDate = (dateISO: string) =>
  new Date(`${dateISO}T00:00:00`).toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });

const formatDateTimeDisplay = (value?: string | null) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const getLeaveDurationDays = (fromISO: string, toISO: string) => {
  const from = parseISOAsUTCDate(fromISO);
  const to = parseISOAsUTCDate(toISO);
  const diffDays = Math.floor((to.getTime() - from.getTime()) / (24 * 60 * 60 * 1000));
  return diffDays + 1;
};

const attendanceButtonClass = (active: boolean, variant: 'present' | 'absent') => {
  if (variant === 'present') {
    return active
      ? 'bg-emerald-500 text-white'
      : 'bg-muted text-muted-foreground hover:bg-emerald-100 hover:text-emerald-700 dark:hover:bg-emerald-900/40';
  }
  return active
    ? 'bg-amber-500 text-white'
    : 'bg-muted text-muted-foreground hover:bg-amber-100 hover:text-amber-700 dark:hover:bg-amber-900/40';
};

const leaveStatusMeta = (status: LeaveStatus) => {
  if (status === 2) {
    return {
      label: 'Approved',
      chipClass:
        'text-emerald-700 border-emerald-300 bg-emerald-50 dark:border-emerald-800 dark:text-emerald-300 dark:bg-emerald-950/40',
    };
  }
  if (status === 3) {
    return {
      label: 'Rejected',
      chipClass:
        'text-red-700 border-red-300 bg-red-50 dark:border-red-800 dark:text-red-300 dark:bg-red-950/40',
    };
  }
  return {
    label: 'Requested',
    chipClass:
      'text-amber-700 border-amber-300 bg-amber-50 dark:border-amber-800 dark:text-amber-300 dark:bg-amber-950/40',
  };
};

const UsersAttendanceContent = ({
  tenant,
  courseId,
  boardId,
  boardName,
  canEdit,
  mode,
  courseGradingStructure = null,
}: UsersAttendanceContentProps) => {
  const [selectedAnalyticsStudentId, setSelectedAnalyticsStudentId] = useState<string | null>(
    null
  );
  const [viewMode, setViewMode] = useState<'table' | 'card'>('table');
  const [searchText, setSearchText] = useState('');
  const [isEnrollDrawerOpen, setIsEnrollDrawerOpen] = useState(false);
  const [drawerSearchText, setDrawerSearchText] = useState('');
  const [selectedCandidateIds, setSelectedCandidateIds] = useState<string[]>([]);
  const [enrollmentToDeleteId, setEnrollmentToDeleteId] = useState<number | null>(null);

  const [selectedAttendanceDate, setSelectedAttendanceDate] = useState(todayISO());
  const [showUnmarkedConfirm, setShowUnmarkedConfirm] = useState(false);
  const [isLeaveDrawerOpen, setIsLeaveDrawerOpen] = useState(false);
  const [selectedLeaveRequestId, setSelectedLeaveRequestId] = useState<number | null>(
    null
  );
  const [isRemarksEditing, setIsRemarksEditing] = useState(false);
  const [remarksDraft, setRemarksDraft] = useState('');

  const {
    data: enrolledStudentsData,
    isLoading: isEnrolledStudentsLoading,
    refetch: refetchEnrolledStudents,
  } = useGetEnrolledStudentsQuery(tenant ? { tenant, courseId, board: '', student: '' } : skipToken);
  const { data: attendanceRatioDataRaw, refetch: refetchAttendanceRatio } =
    useGetStudentAttendanceQuery(tenant ? { tenant, courseId } : skipToken);
  const { data: boardStudentsData, isLoading: isBoardStudentsLoading } =
    useGetEnrolledStudentsQuery(
      tenant && isEnrollDrawerOpen && boardId
        ? { tenant, board: boardId, courseId: '', student: '' }
        : skipToken
    );
  const {
    data: attendanceRecordsData,
    isLoading: isAttendanceRecordsLoading,
    refetch: refetchAttendanceRecords,
  } = useGetStudentsAttendanceByDateQuery(
    tenant && mode === 'attendance'
      ? { tenant, courseId, attendanceDate: selectedAttendanceDate }
      : skipToken
  );
  const {
    data: leaveRequestsData,
    isLoading: isLeaveRequestsLoading,
    refetch: refetchLeaveRequests,
  } =
    useGetRequestLeaveByDateQuery(
      tenant && mode === 'attendance'
        ? { tenant, courseId, dateInRange: selectedAttendanceDate }
        : skipToken
    );
  const { data: courseReportDataRaw } = useGetCourseReportQuery(
    tenant && mode === 'users' ? { tenant, courseId } : skipToken
  );
  const { data: courseMarkingSchemeDataRaw } = useGetCourseMarkingSchemeQuery(
    tenant && mode === 'users' ? { tenant, courseId } : skipToken
  );
  const enrolledStudents = enrolledStudentsData ?? EMPTY_ARRAY;
  const attendanceRatioData = attendanceRatioDataRaw ?? EMPTY_ARRAY;
  const boardStudents = boardStudentsData ?? EMPTY_ARRAY;
  const attendanceRecords = attendanceRecordsData ?? EMPTY_ARRAY;
  const leaveRequests = leaveRequestsData ?? EMPTY_ARRAY;
  const courseReportData = courseReportDataRaw ?? EMPTY_ARRAY;
  const courseMarkingSchemeData = courseMarkingSchemeDataRaw ?? EMPTY_ARRAY;

  const [createEnrolledStudents, { isLoading: isCreatingEnrollments }] =
    useCreateEnrolledStudentsMutation();
  const [patchEnrolledStudent, { isLoading: isPatchingEnrollment }] =
    usePatchEnrolledStudentMutation();
  const [deleteEnrolledStudent, { isLoading: isDeletingEnrollment }] =
    useDeleteEnrolledStudentMutation();
  const [createStudentsAttendance, { isLoading: isCreatingAttendance }] =
    useCreateStudentsAttendanceMutation();
  const [updateStudentsAttendance, { isLoading: isUpdatingAttendance }] =
    useUpdateStudentsAttendanceMutation();
  const [patchAcceptLeave, { isLoading: isPatchingLeaveRequest }] =
    usePatchAcceptLeaveMutation();

  const attendanceRatioByStudentId = useMemo(() => {
    const map = new Map<string, (typeof attendanceRatioData)[number]>();
    attendanceRatioData.forEach((item) => {
      map.set(item.student_id, item);
    });
    return map;
  }, [attendanceRatioData]);

  const attendanceRecordByStudentId = useMemo(() => {
    const map = new Map<string, (typeof attendanceRecords)[number]>();
    attendanceRecords.forEach((item) => {
      map.set(item.student, item);
    });
    return map;
  }, [attendanceRecords]);

  const leaveRequestByStudentId = useMemo(() => {
    const map = new Map<string, (typeof leaveRequests)[number]>();
    leaveRequests.forEach((item) => {
      map.set(item.student, item);
    });
    return map;
  }, [leaveRequests]);

  const studentScorePercentageById = useMemo(() => {
    const map = new Map<string, number>();
    const hasAttendanceContributionFromReport = new Set<string>();
    const markingSchemeById = new Map(
      courseMarkingSchemeData.map((item) => [item.id, item] as const)
    );

    courseReportData.forEach((reportItem) => {
      const mappedMarkingScheme = markingSchemeById.get(reportItem.id);
      const isAttendanceStructure =
        mappedMarkingScheme?.course_structure?.assessment_type === 'Attendance' &&
        mappedMarkingScheme?.course_sub_structure === null;

      reportItem.student.forEach((studentRow) => {
        const studentId = studentRow.student?.id;
        if (!studentId) return;

        let score = Number(studentRow.acquired_percentage);
        if (!Number.isFinite(score)) {
          const marks = Number(studentRow.marks);
          const fullMarks = Number(studentRow.full_marks);
          const weightage = Number(reportItem.course_percentage);
          score =
            Number.isFinite(marks) &&
            Number.isFinite(fullMarks) &&
            fullMarks > 0 &&
            Number.isFinite(weightage)
              ? (marks / fullMarks) * weightage
              : 0;
        }

        map.set(studentId, (map.get(studentId) ?? 0) + score);
        if (isAttendanceStructure) {
          hasAttendanceContributionFromReport.add(studentId);
        }
      });
    });

    const attendanceMarkingScheme = courseMarkingSchemeData.find(
      (item) =>
        item.course_structure.assessment_type === 'Attendance' &&
        item.course_sub_structure === null
    );
    const attendanceWeight = Number(attendanceMarkingScheme?.course_percentage ?? 0);

    if (attendanceWeight > 0) {
      attendanceRatioData.forEach((ratio) => {
        if (hasAttendanceContributionFromReport.has(ratio.student_id)) return;
        const attendanceRatio = Number(ratio.attendance_ratio);
        if (!Number.isFinite(attendanceRatio)) return;
        const attendanceContribution = (attendanceRatio / 100) * attendanceWeight;
        map.set(
          ratio.student_id,
          (map.get(ratio.student_id) ?? 0) + attendanceContribution
        );
      });
    }

    return map;
  }, [attendanceRatioData, courseMarkingSchemeData, courseReportData]);

  const studentGradeById = useMemo(() => {
    const map = new Map<string, string>();
    const grades = courseGradingStructure?.grades ?? [];
    if (!grades.length) return map;
    studentScorePercentageById.forEach((score, studentId) => {
      const matched = grades.find((item) => {
        const lower = Number(item.lower_threshold);
        const upper = Number(item.upper_threshold);
        const min = Math.min(lower, upper);
        const max = Math.max(lower, upper);
        return score >= min && score <= max;
      });
      map.set(studentId, matched?.grade ?? 'N/A');
    });
    return map;
  }, [courseGradingStructure?.grades, studentScorePercentageById]);

  const formatScorePercentage = (value: number | undefined) => {
    if (value === undefined || Number.isNaN(value)) return 'N/A';
    return `${Number(value).toFixed(2)}%`;
  };
  const selectedLeaveRequest = useMemo(
    () => leaveRequests.find((item) => item.id === selectedLeaveRequestId) ?? null,
    [leaveRequests, selectedLeaveRequestId]
  );
  const selectedLeaveEnrollment = useMemo(
    () =>
      enrolledStudents.find(
        (entry) => entry.student.id === selectedLeaveRequest?.student
      ) ?? null,
    [enrolledStudents, selectedLeaveRequest?.student]
  );
  const remarksGetterRef = useRef<(() => string) | null>(null);

  const initialAttendanceByStudentId = useMemo(() => {
    const next: Record<string, AttendanceStatus> = {};
    enrolledStudents.forEach((entry) => {
      const attendance = attendanceRecordByStudentId.get(entry.student.id);
      if (attendance?.attendance_status === 1 || attendance?.attendance_status === 2) {
        next[entry.student.id] = attendance.attendance_status;
      } else {
        next[entry.student.id] = null;
      }
    });
    return next;
  }, [attendanceRecordByStudentId, enrolledStudents]);
  const attendanceSourceKey = useMemo(
    () => JSON.stringify(initialAttendanceByStudentId),
    [initialAttendanceByStudentId]
  );
  const [attendanceDraftState, setAttendanceDraftState] = useState<{
    sourceKey: string;
    draft: Record<string, AttendanceStatus>;
  }>({
    sourceKey: attendanceSourceKey,
    draft: initialAttendanceByStudentId,
  });
  useEffect(() => {
    setAttendanceDraftState({
      sourceKey: attendanceSourceKey,
      draft: initialAttendanceByStudentId,
    });
  }, [attendanceSourceKey, initialAttendanceByStudentId]);
  const draftAttendanceByStudentId =
    attendanceDraftState.sourceKey === attendanceSourceKey
      ? attendanceDraftState.draft
      : initialAttendanceByStudentId;
  const setDraftAttendanceByStudentId = (
    updater: SetStateAction<Record<string, AttendanceStatus>>
  ) => {
    setAttendanceDraftState((prev) => {
      const base =
        prev.sourceKey === attendanceSourceKey
          ? prev.draft
          : initialAttendanceByStudentId;
      const nextDraft =
        typeof updater === 'function'
          ? (updater as (prevState: Record<string, AttendanceStatus>) => Record<string, AttendanceStatus>)(base)
          : updater;
      return {
        sourceKey: attendanceSourceKey,
        draft: nextDraft,
      };
    });
  };

  const filteredEnrolledStudents = useMemo(() => {
    const q = searchText.trim().toLowerCase();
    if (!q) return enrolledStudents;
    return enrolledStudents.filter((item) => {
      const fullName = `${item.student.first_name} ${item.student.last_name}`.toLowerCase();
      return fullName.includes(q) || item.student.email.toLowerCase().includes(q);
    });
  }, [enrolledStudents, searchText]);

  const candidateStudents = useMemo(() => {
    const existingIds = new Set(enrolledStudents.map((item) => item.student.id));
    const q = drawerSearchText.trim().toLowerCase();
    return boardStudents.filter((item) => {
      if (existingIds.has(item.student.id)) return false;
      if (!q) return true;
      const fullName = `${item.student.first_name} ${item.student.last_name}`.toLowerCase();
      return fullName.includes(q) || item.student.email.toLowerCase().includes(q);
    });
  }, [boardStudents, enrolledStudents, drawerSearchText]);

  const allVisibleCandidatesSelected =
    candidateStudents.length > 0 &&
    candidateStudents.every((entry) => selectedCandidateIds.includes(entry.student.id));

  const handleSelectAllCandidatesChange = (checked: boolean | 'indeterminate') => {
    const shouldSelectAll = checked === true;

    if (shouldSelectAll) {
      candidateStudents.forEach((entry) => {
        if (!selectedCandidateIds.includes(entry.student.id)) {
          setSelectedCandidateIds((prev) => [...prev, entry.student.id]);
        }
      });
      return;
    }

    candidateStudents.forEach((entry) => {
      if (selectedCandidateIds.includes(entry.student.id)) {
        setSelectedCandidateIds((prev) => prev.filter((id) => id !== entry.student.id));
      }
    });
  };

  const enrollmentToDelete = useMemo(
    () => enrolledStudents.find((item) => item.id === enrollmentToDeleteId) ?? null,
    [enrolledStudents, enrollmentToDeleteId]
  );
  const hasAttendanceChanges = useMemo(() => {
    return enrolledStudents.some((entry) => {
      const studentId = entry.student.id;
      const current = draftAttendanceByStudentId[studentId] ?? null;
      const initial = initialAttendanceByStudentId[studentId] ?? null;
      return current !== initial;
    });
  }, [draftAttendanceByStudentId, enrolledStudents, initialAttendanceByStudentId]);

  const attendanceSummary = useMemo(() => {
    let presentCount = 0;
    let absentCount = 0;
    filteredEnrolledStudents.forEach((entry) => {
      const status = draftAttendanceByStudentId[entry.student.id] ?? null;
      if (status === 1) presentCount += 1;
      if (status === 2) absentCount += 1;
    });
    const total = filteredEnrolledStudents.length;
    const marked = presentCount + absentCount;
    const percentage = total > 0 ? Math.round((presentCount / total) * 100) : 0;
    return { presentCount, absentCount, total, marked, percentage };
  }, [draftAttendanceByStudentId, filteredEnrolledStudents]);

  const isSavingAttendance = isCreatingAttendance || isUpdatingAttendance;
  const isFutureDate = selectedAttendanceDate > todayISO();

  const openLeaveRequestDrawer = (leaveRequest: LeaveRequestRecord) => {
    setSelectedLeaveRequestId(leaveRequest.id);
    setRemarksDraft(leaveRequest.remarks || '');
    setIsRemarksEditing(false);
    setIsLeaveDrawerOpen(true);
  };

  const handlePatchLeaveStatus = async (leaveId: number, nextStatus: 2 | 3) => {
    try {
      await patchAcceptLeave({
        tenant,
        leaveId,
        data: { request_status: nextStatus },
      }).unwrap();
      await refetchLeaveRequests();
      toast.success(nextStatus === 2 ? 'Leave request accepted' : 'Leave request rejected');
    } catch {
      toast.error('Failed to update leave request status');
    }
  };

  const handleSaveRemarks = async () => {
    if (!selectedLeaveRequestId) return;
    try {
      const remarksValue = remarksGetterRef.current?.() || remarksDraft;
      await patchAcceptLeave({
        tenant,
        leaveId: selectedLeaveRequestId,
        data: { remarks: remarksValue },
      }).unwrap();
      await refetchLeaveRequests();
      setRemarksDraft(remarksValue);
      setIsRemarksEditing(false);
      toast.success('Remarks updated successfully');
    } catch {
      toast.error('Failed to update remarks');
    }
  };

  const handleToggleEnrollmentActive = async (enrollmentId: number, currentActive: boolean) => {
    try {
      const nextActive = !currentActive;
      await patchEnrolledStudent({
        tenant,
        enrollmentId,
        courseId,
        data: { active: nextActive, semester_course: courseId },
      }).unwrap();
      toast.success(nextActive ? 'User marked as active' : 'User marked as inactive');
    } catch {
      toast.error('Failed to update user status');
    }
  };

  const handleDeleteEnrollment = async () => {
    if (!enrollmentToDeleteId) return;
    try {
      await deleteEnrolledStudent({
        tenant,
        enrollmentId: enrollmentToDeleteId,
        courseId,
      }).unwrap();
      setEnrollmentToDeleteId(null);
      toast.success('User removed successfully');
    } catch {
      toast.error('Failed to remove user');
    }
  };

  const handleSaveEnrollments = async () => {
    if (!selectedCandidateIds.length) return;
    try {
      await createEnrolledStudents({
        tenant,
        data: {
          students: selectedCandidateIds.map((studentId) => ({
            student: studentId,
            board: null,
            semester_course: courseId,
            active: true,
          })),
        },
      }).unwrap();
      setSelectedCandidateIds([]);
      setDrawerSearchText('');
      setIsEnrollDrawerOpen(false);
      void refetchEnrolledStudents();
      toast.success('Users enrolled successfully');
    } catch {
      toast.error('Failed to enroll users');
    }
  };

  const setStudentAttendance = (studentId: string, status: AttendanceStatus) => {
    setDraftAttendanceByStudentId((prev) => ({
      ...prev,
      [studentId]: status,
    }));
  };

  const markAllAttendance = (status: 1 | 2) => {
    setDraftAttendanceByStudentId((prev) => {
      const next = { ...prev };
      enrolledStudents.forEach((entry) => {
        next[entry.student.id] = status;
      });
      return next;
    });
  };

  const resetAttendanceDraft = () => {
    setDraftAttendanceByStudentId(initialAttendanceByStudentId);
  };

  const executeSaveAttendance = async () => {
    try {
      const createItems: {
        attendance_date: string;
        student: string;
        course: string;
        attendance_status: number;
      }[] = [];
      const updateItems: {
        attendance_date: string;
        student: string;
        course: string;
        attendance_status: number;
        id: number;
      }[] = [];

      enrolledStudents.forEach((entry) => {
        const studentId = entry.student.id;
        const status = draftAttendanceByStudentId[studentId] ?? 2;
        const existingRecord = attendanceRecordByStudentId.get(studentId);
        const payloadBase = {
          attendance_date: selectedAttendanceDate,
          student: studentId,
          course: String(courseId),
          attendance_status: status,
        };
        if (existingRecord?.id) {
          updateItems.push({
            ...payloadBase,
            id: existingRecord.id,
          });
        } else {
          createItems.push(payloadBase);
        }
      });

      if (updateItems.length) {
        await updateStudentsAttendance({
          tenant,
          data: {
            students: updateItems,
            courseId: String(courseId),
          },
        }).unwrap();
      }

      if (createItems.length) {
        await createStudentsAttendance({
          tenant,
          courseId,
          data: {
            students: createItems,
          },
        }).unwrap();
      }

      await refetchAttendanceRecords();
      await refetchAttendanceRatio();
      toast.success('Attendance saved successfully');
    } catch {
      toast.error('Failed to save attendance');
    }
  };

  const handleSaveAttendance = async () => {
    if (!hasAttendanceChanges) return;
    const hasUnmarked = enrolledStudents.some((entry) => {
      const status = draftAttendanceByStudentId[entry.student.id] ?? null;
      return status === null;
    });

    if (hasUnmarked) {
      setShowUnmarkedConfirm(true);
      return;
    }

    await executeSaveAttendance();
  };

  if (mode === 'attendance') {
    return (
      <div className="h-full min-h-[420px] rounded-xl border bg-card p-4 sm:p-6 shadow-sm space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="inline-flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setSelectedAttendanceDate((prev) => addDaysISO(prev, -1))}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <p className="text-lg font-medium">{formatDisplayDate(selectedAttendanceDate)}</p>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setSelectedAttendanceDate((prev) => addDaysISO(prev, 1))}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {canEdit && (
              <>
                <Button
                  variant="outline"
                  className="border-emerald-500 text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-950/30"
                  onClick={() => markAllAttendance(1)}
                  disabled={isFutureDate}
                >
                  Mark all present
                </Button>
                <Button
                  variant="outline"
                  className="border-amber-500 text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-950/30"
                  onClick={() => markAllAttendance(2)}
                  disabled={isFutureDate}
                >
                  Mark all absent
                </Button>
                {hasAttendanceChanges && (
                  <>
                    <Button variant="outline" onClick={resetAttendanceDraft}>
                      Reset
                    </Button>
                    <Button
                      onClick={() => void handleSaveAttendance()}
                      disabled={isSavingAttendance || isFutureDate}
                    >
                      Save
                    </Button>
                  </>
                )}
              </>
            )}
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="text-sm text-muted-foreground">
            Users: {filteredEnrolledStudents.length} | Attendance{' '}
            <span className="rounded-full bg-muted px-2 py-0.5 text-foreground">
              {attendanceSummary.percentage}%
            </span>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                placeholder="Search user by name"
                className="h-10 w-full min-w-0 pl-9 sm:w-[230px]"
              />
            </div>
            <div className="inline-flex items-center rounded-md border bg-muted/20 p-1">
              <button
                type="button"
                onClick={() => setViewMode('table')}
                className={`inline-flex h-8 w-8 items-center justify-center rounded ${
                  viewMode === 'table' ? 'bg-primary/12 text-primary' : 'text-muted-foreground'
                }`}
                aria-label="Table view"
              >
                <List className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() => setViewMode('card')}
                className={`inline-flex h-8 w-8 items-center justify-center rounded ${
                  viewMode === 'card' ? 'bg-primary/12 text-primary' : 'text-muted-foreground'
                }`}
                aria-label="Card view"
              >
                <LayoutGrid className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>

        {isEnrolledStudentsLoading || isAttendanceRecordsLoading || isLeaveRequestsLoading ? (
          <div className="text-sm text-muted-foreground">Loading attendance...</div>
        ) : filteredEnrolledStudents.length === 0 ? (
          <div className="rounded-xl border border-dashed p-8 text-center text-muted-foreground">
            No enrolled users found.
          </div>
        ) : viewMode === 'table' ? (
          <div className="overflow-x-auto rounded-xl border">
            <table className="w-full min-w-[980px] text-sm">
              <thead className="bg-muted/50 text-left">
                <tr>
                  <th className="px-4 py-3 font-medium">
                    Users <span className="text-muted-foreground">({filteredEnrolledStudents.length})</span>
                  </th>
                  <th className="px-4 py-3 font-medium">Attendance</th>
                  <th className="px-4 py-3 font-medium">Attendance ratio</th>
                  <th className="px-4 py-3 font-medium">Leave requests</th>
                </tr>
              </thead>
              <tbody>
                {filteredEnrolledStudents.map((entry) => {
                  const fullName = `${entry.student.first_name} ${entry.student.last_name}`;
                  const status = draftAttendanceByStudentId[entry.student.id] ?? null;
                  const ratio = attendanceRatioByStudentId.get(entry.student.id);
                  const leaveRequest = leaveRequestByStudentId.get(entry.student.id);
                  const leaveStatus = (leaveRequest?.request_status || 1) as LeaveStatus;
                  const leaveMeta = leaveStatusMeta(leaveStatus);
                  return (
                    <tr key={entry.id} className={`border-t ${entry.active ? '' : 'opacity-60'}`}>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <Avatar className="h-10 w-10">
                            <AvatarImage src={entry.student.photo_url || undefined} alt={fullName} />
                            <AvatarFallback>
                              {getInitials(entry.student.first_name, entry.student.last_name)}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <p className="font-medium">{fullName}</p>
                            <p className="text-xs text-muted-foreground">{entry.student.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="inline-flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => setStudentAttendance(entry.student.id, 1)}
                            disabled={isFutureDate}
                            className={`h-9 w-9 rounded-full text-sm font-semibold transition-colors ${attendanceButtonClass(
                              status === 1,
                              'present'
                            )}`}
                          >
                            P
                          </button>
                          <button
                            type="button"
                            onClick={() => setStudentAttendance(entry.student.id, 2)}
                            disabled={isFutureDate}
                            className={`h-9 w-9 rounded-full text-sm font-semibold transition-colors ${attendanceButtonClass(
                              status === 2,
                              'absent'
                            )}`}
                          >
                            A
                          </button>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        {ratio ? (
                          <span>
                            <span className="font-medium text-foreground">
                              {Number(ratio.attendance_ratio).toFixed(1)}%
                            </span>
                            <span className="text-muted-foreground">
                              {' '}
                              | {ratio.total_present_days}/{ratio.total_working_days}
                            </span>
                          </span>
                        ) : (
                          '-'
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {leaveRequest ? (
                          <div className="flex flex-wrap items-center gap-2">
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className={leaveMeta.chipClass}
                              onClick={() => openLeaveRequestDrawer(leaveRequest)}
                            >
                              <CircleAlert className="h-4 w-4" />
                              {`Leave ${leaveMeta.label.toLowerCase()}`}
                            </Button>
                          </div>
                        ) : (
                          <span className="text-muted-foreground">No request</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
            {filteredEnrolledStudents.map((entry) => {
              const fullName = `${entry.student.first_name} ${entry.student.last_name}`;
              const status = draftAttendanceByStudentId[entry.student.id] ?? null;
              const ratio = attendanceRatioByStudentId.get(entry.student.id);
              const leaveRequest = leaveRequestByStudentId.get(entry.student.id);
              const leaveStatus = (leaveRequest?.request_status || 1) as LeaveStatus;
              const leaveMeta = leaveStatusMeta(leaveStatus);
              return (
                <div
                  key={entry.id}
                  className={`rounded-xl border bg-background overflow-hidden shadow-sm ${
                    entry.active ? '' : 'opacity-60 saturate-50'
                  }`}
                >
                  <div className="h-20 bg-gradient-to-r from-slate-700 to-slate-800 px-4 py-3 text-white/90 text-sm">
                    Learner
                  </div>
                  <div className="px-4 pb-5 -mt-10 flex flex-col items-center text-center">
                    <Avatar className="h-20 w-20 border-4 border-background">
                      <AvatarImage src={entry.student.photo_url || undefined} alt={fullName} />
                      <AvatarFallback>
                        {getInitials(entry.student.first_name, entry.student.last_name)}
                      </AvatarFallback>
                    </Avatar>
                    <p className="mt-3 text-lg font-semibold">{fullName}</p>
                    <p className="text-sm text-muted-foreground break-all">{entry.student.email}</p>
                    <p className="mt-2 text-xs text-muted-foreground">
                      {ratio
                        ? `Attendance ratio: ${Number(ratio.attendance_ratio).toFixed(1)}%`
                        : 'Attendance ratio: -'}
                    </p>
                    <div className="mt-3 inline-flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => setStudentAttendance(entry.student.id, 1)}
                        disabled={isFutureDate}
                        className={`h-9 w-9 rounded-full text-sm font-semibold transition-colors ${attendanceButtonClass(
                          status === 1,
                          'present'
                        )}`}
                      >
                        P
                      </button>
                      <button
                        type="button"
                        onClick={() => setStudentAttendance(entry.student.id, 2)}
                        disabled={isFutureDate}
                        className={`h-9 w-9 rounded-full text-sm font-semibold transition-colors ${attendanceButtonClass(
                          status === 2,
                          'absent'
                        )}`}
                      >
                        A
                      </button>
                    </div>
                    <div className="mt-3">
                      {leaveRequest ? (
                        <div className="flex flex-wrap items-center justify-center gap-2">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className={leaveMeta.chipClass}
                            onClick={() => openLeaveRequestDrawer(leaveRequest)}
                          >
                            <CircleAlert className="h-4 w-4" />
                            {`Leave ${leaveMeta.label.toLowerCase()}`}
                          </Button>
                        </div>
                      ) : (
                        <span className="text-xs text-muted-foreground">No request</span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <Sheet
          open={isLeaveDrawerOpen}
          onOpenChange={(open) => {
            setIsLeaveDrawerOpen(open);
            if (!open) {
              setSelectedLeaveRequestId(null);
              setIsRemarksEditing(false);
              setRemarksDraft('');
            }
          }}
        >
          <SheetContent side="right" className="w-full p-0 sm:max-w-2xl">
            <SheetHeader className="border-b p-4">
              <SheetTitle>Leave request</SheetTitle>
              <SheetDescription>
                Submitted on: {formatDateTimeDisplay(selectedLeaveRequest?.created_date)}
              </SheetDescription>
            </SheetHeader>

            <div className="flex-1 overflow-y-auto p-4">
              {selectedLeaveRequest ? (
                <div className="space-y-4 rounded-xl border bg-background p-4">
                  <div className="flex flex-wrap items-start justify-between gap-4 border-b pb-4">
                    <div className="flex items-center gap-3">
                      <Avatar className="h-16 w-16 border">
                        <AvatarImage
                          src={selectedLeaveEnrollment?.student.photo_url || undefined}
                          alt={
                            selectedLeaveEnrollment
                              ? `${selectedLeaveEnrollment.student.first_name} ${selectedLeaveEnrollment.student.last_name}`
                              : 'Student'
                          }
                        />
                        <AvatarFallback>
                          {selectedLeaveEnrollment
                            ? getInitials(
                                selectedLeaveEnrollment.student.first_name,
                                selectedLeaveEnrollment.student.last_name
                              )
                            : 'S'}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="text-lg font-semibold">
                          {selectedLeaveEnrollment
                            ? `${selectedLeaveEnrollment.student.first_name} ${selectedLeaveEnrollment.student.last_name}`
                            : 'Student'}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {selectedLeaveEnrollment?.student.email || '-'}
                        </p>
                      </div>
                    </div>
                    <div className="min-w-[210px] space-y-1 text-sm">
                      <p>
                        <span className="text-muted-foreground">From:</span>{' '}
                        <span className="font-medium">
                          {formatDisplayDate(selectedLeaveRequest.leave_from)}
                        </span>
                      </p>
                      <p>
                        <span className="text-muted-foreground">To:</span>{' '}
                        <span className="font-medium">
                          {formatDisplayDate(selectedLeaveRequest.leave_to)}
                        </span>
                      </p>
                      <p>
                        <span className="text-muted-foreground">Duration:</span>{' '}
                        <span className="font-medium">
                          {getLeaveDurationDays(
                            selectedLeaveRequest.leave_from,
                            selectedLeaveRequest.leave_to
                          )}{' '}
                          days
                        </span>
                      </p>
                      <p>
                        <span className="text-muted-foreground">Status:</span>{' '}
                        <span
                          className={`inline-flex rounded-full border px-2 py-0.5 text-xs font-medium ${
                            leaveStatusMeta(
                              (selectedLeaveRequest.request_status || 1) as LeaveStatus
                            ).chipClass
                          }`}
                        >
                          {
                            leaveStatusMeta(
                              (selectedLeaveRequest.request_status || 1) as LeaveStatus
                            ).label
                          }
                        </span>
                      </p>
                    </div>
                    {canEdit && (
                      <div className="flex items-center gap-2">
                        {((selectedLeaveRequest.request_status || 1) as LeaveStatus) !== 2 && (
                          <Button
                            type="button"
                            variant="outline"
                            className="border-blue-300 text-blue-600 hover:bg-blue-50 dark:border-blue-800 dark:text-blue-300 dark:hover:bg-blue-950/30"
                            disabled={isPatchingLeaveRequest}
                            onClick={() =>
                              void handlePatchLeaveStatus(selectedLeaveRequest.id, 2)
                            }
                          >
                            <Check className="h-4 w-4" />
                            Accept
                          </Button>
                        )}
                        {((selectedLeaveRequest.request_status || 1) as LeaveStatus) !== 3 && (
                          <Button
                            type="button"
                            variant="outline"
                            className="border-red-300 text-red-600 hover:bg-red-50 dark:border-red-800 dark:text-red-300 dark:hover:bg-red-950/30"
                            disabled={isPatchingLeaveRequest}
                            onClick={() =>
                              void handlePatchLeaveStatus(selectedLeaveRequest.id, 3)
                            }
                          >
                            <X className="h-4 w-4" />
                            Reject
                          </Button>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="space-y-2">
                    <h4 className="font-semibold">Details</h4>
                    {selectedLeaveRequest.leave_reason ? (
                      <ReadOnlyContentDisplay
                        html={selectedLeaveRequest.leave_reason}
                        className="[&_.editor-input]:!min-h-0 [&_.editor-input]:!p-0 [&_.editor-input]:text-sm"
                      />
                    ) : (
                      <p className="text-sm text-muted-foreground">No details provided.</p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <h4 className="font-semibold">Attachments</h4>
                    {selectedLeaveRequest.supporting_document ? (
                      <a
                        href={selectedLeaveRequest.supporting_document}
                        target="_blank"
                        rel="noreferrer"
                        className="text-sm text-primary hover:underline"
                      >
                        View attachment
                      </a>
                    ) : (
                      <p className="text-sm text-muted-foreground">No attachment</p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center justify-between gap-2">
                      <h4 className="font-semibold">Remarks</h4>
                      {!isRemarksEditing && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setRemarksDraft(selectedLeaveRequest.remarks || '');
                            setIsRemarksEditing(true);
                          }}
                        >
                          <Pencil className="h-4 w-4" />
                          {selectedLeaveRequest.remarks ? 'Edit' : 'Add remarks'}
                        </Button>
                      )}
                    </div>
                    {isRemarksEditing ? (
                      <div className="space-y-3">
                        <RichTextEditor
                          value={remarksDraft}
                          onChange={setRemarksDraft}
                          placeholder="Add remarks..."
                          minHeight={180}
                          onProvideGetSerializedState={(getSerializedState) => {
                            remarksGetterRef.current = getSerializedState;
                          }}
                        />
                        <div className="flex items-center gap-2">
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => {
                              setRemarksDraft(selectedLeaveRequest.remarks || '');
                              setIsRemarksEditing(false);
                            }}
                          >
                            Cancel
                          </Button>
                          <Button
                            type="button"
                            onClick={() => void handleSaveRemarks()}
                            disabled={isPatchingLeaveRequest}
                          >
                            Save
                          </Button>
                        </div>
                      </div>
                    ) : selectedLeaveRequest.remarks ? (
                      <div className="space-y-2">
                        <ReadOnlyContentDisplay
                          html={selectedLeaveRequest.remarks}
                          className="[&_.editor-input]:!min-h-0 [&_.editor-input]:!p-0 [&_.editor-input]:text-sm"
                        />
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground">No remarks added.</p>
                    )}
                  </div>
                </div>
              ) : (
                <div className="text-sm text-muted-foreground">No leave request selected.</div>
              )}
            </div>
          </SheetContent>
        </Sheet>
      </div>
    );
  }

  return (
    <div className="h-full min-h-[420px] rounded-xl border bg-card p-4 sm:p-6 shadow-sm space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          {canEdit && (
            <Button onClick={() => setIsEnrollDrawerOpen(true)} disabled={!boardId}>
              <Plus className="h-4 w-4" />
              Enroll new user
            </Button>
          )}
        </div>

          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                placeholder="Search user by name"
                className="h-10 w-full min-w-0 pl-9 sm:w-[230px]"
              />
            </div>
          <div className="inline-flex items-center rounded-md border bg-muted/20 p-1">
            <button
              type="button"
              onClick={() => setViewMode('table')}
              className={`inline-flex h-8 w-8 items-center justify-center rounded ${
                viewMode === 'table' ? 'bg-primary/12 text-primary' : 'text-muted-foreground'
              }`}
              aria-label="Table view"
            >
              <List className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={() => setViewMode('card')}
              className={`inline-flex h-8 w-8 items-center justify-center rounded ${
                viewMode === 'card' ? 'bg-primary/12 text-primary' : 'text-muted-foreground'
              }`}
              aria-label="Card view"
            >
              <LayoutGrid className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      {selectedAnalyticsStudentId ? (
        <div className="space-y-4">
          <button
            type="button"
            onClick={() => setSelectedAnalyticsStudentId(null)}
            className="inline-flex items-center gap-2 text-sm text-primary hover:underline"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to users table
          </button>
          <CourseStudentAnalyticsPanel
            tenant={tenant}
            courseId={courseId}
            studentId={selectedAnalyticsStudentId}
            courseGradingStructure={courseGradingStructure}
            canEdit={canEdit}
          />
        </div>
      ) : isEnrolledStudentsLoading ? (
        <div className="text-sm text-muted-foreground">Loading users...</div>
      ) : filteredEnrolledStudents.length === 0 ? (
        <div className="rounded-xl border border-dashed p-8 text-center text-muted-foreground">
          No enrolled users found.
        </div>
      ) : viewMode === 'table' ? (
        <div className="overflow-x-auto rounded-xl border">
          <table className="w-full min-w-[980px] text-sm">
            <thead className="bg-muted/50 text-left">
              <tr>
                <th className="px-4 py-3 font-medium">
                  Users <span className="text-muted-foreground">({filteredEnrolledStudents.length})</span>
                </th>
                <th className="px-4 py-3 font-medium">Attendance ratio</th>
                <th className="px-4 py-3 font-medium">% Score</th>
                <th className="px-4 py-3 font-medium">Grade</th>
                <th className="px-4 py-3 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredEnrolledStudents.map((entry) => {
                const attendance = attendanceRatioByStudentId.get(entry.student.id);
                const scorePercentage = studentScorePercentageById.get(entry.student.id);
                const mappedGrade = courseGradingStructure
                  ? studentGradeById.get(entry.student.id) ?? 'N/A'
                  : 'N/A';
                const fullName = `${entry.student.first_name} ${entry.student.last_name}`;
                return (
                  <tr
                    key={entry.id}
                    className={`border-t transition-colors hover:bg-muted/20 ${entry.active ? '' : 'opacity-60'} ${mode === 'users' ? 'cursor-pointer' : ''}`}
                    onClick={() => {
                      if (mode !== 'users') return;
                      setSelectedAnalyticsStudentId(entry.student.id);
                    }}
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <Avatar className="h-10 w-10">
                          <AvatarImage src={entry.student.photo_url || undefined} alt={fullName} />
                          <AvatarFallback>
                            {getInitials(entry.student.first_name, entry.student.last_name)}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="font-medium">{fullName}</p>
                          <p className="text-xs text-muted-foreground">{entry.student.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {attendance ? (
                        <span>
                          <span className="font-medium text-foreground">
                            {Number(attendance.attendance_ratio).toFixed(1)}%
                          </span>
                          <span className="text-muted-foreground">
                            {' '}
                            | {attendance.total_present_days}/{attendance.total_working_days}
                          </span>
                        </span>
                      ) : (
                        '-'
                      )}
                    </td>
                    <td className="px-4 py-3">{formatScorePercentage(scorePercentage)}</td>
                    <td className="px-4 py-3">{mappedGrade}</td>
                    <td className="px-4 py-3">
                      {canEdit ? (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button
                              type="button"
                              className="inline-flex h-8 w-8 items-center justify-center rounded-md hover:bg-muted"
                              onClick={(event) => event.stopPropagation()}
                            >
                              <SlidersHorizontal className="h-4 w-4" />
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              disabled={isPatchingEnrollment}
                              onClick={(event) => {
                                event.stopPropagation();
                                void handleToggleEnrollmentActive(entry.id, entry.active);
                              }}
                            >
                              {entry.active ? 'Mark as Inactive' : 'Mark as Active'}
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              variant="destructive"
                              disabled={isDeletingEnrollment}
                              onClick={(event) => {
                                event.stopPropagation();
                                setEnrollmentToDeleteId(entry.id);
                              }}
                            >
                              Remove
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      ) : (
                        '-'
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
          {filteredEnrolledStudents.map((entry) => {
            const fullName = `${entry.student.first_name} ${entry.student.last_name}`;
            const attendance = attendanceRatioByStudentId.get(entry.student.id);
            const scorePercentage = studentScorePercentageById.get(entry.student.id);
            const mappedGrade = courseGradingStructure
              ? studentGradeById.get(entry.student.id) ?? 'N/A'
              : 'N/A';
            return (
              <div
                key={entry.id}
                className={`rounded-xl border bg-background overflow-hidden shadow-sm ${
                  entry.active ? '' : 'opacity-60 saturate-50'
                } cursor-pointer`}
                onClick={() => setSelectedAnalyticsStudentId(entry.student.id)}
              >
                <div className="h-20 bg-gradient-to-r from-slate-700 to-slate-800 px-4 py-3 text-white/90 text-sm flex items-start justify-between">
                  <span>Learner</span>
                  {canEdit ? (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button
                          type="button"
                          className="h-7 w-7 inline-flex items-center justify-center rounded-md hover:bg-white/15"
                          onClick={(event) => event.stopPropagation()}
                        >
                          <SlidersHorizontal className="h-4 w-4" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem
                          disabled={isPatchingEnrollment}
                          onClick={(event) => {
                            event.stopPropagation();
                            void handleToggleEnrollmentActive(entry.id, entry.active);
                          }}
                        >
                          {entry.active ? 'Mark as Inactive' : 'Mark as Active'}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          variant="destructive"
                          disabled={isDeletingEnrollment}
                          onClick={(event) => {
                            event.stopPropagation();
                            setEnrollmentToDeleteId(entry.id);
                          }}
                        >
                          Remove
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  ) : null}
                </div>
                <div className="px-4 pb-5 -mt-10 flex flex-col items-center text-center">
                  <Avatar className="h-20 w-20 border-4 border-background">
                    <AvatarImage src={entry.student.photo_url || undefined} alt={fullName} />
                    <AvatarFallback>
                      {getInitials(entry.student.first_name, entry.student.last_name)}
                    </AvatarFallback>
                  </Avatar>
                  <p className="mt-3 text-lg font-semibold">{fullName}</p>
                  <p className="text-sm text-muted-foreground break-all">{entry.student.email}</p>
                  <p className="mt-2 text-xs text-muted-foreground">
                    Attendance:{' '}
                    {attendance ? `${Number(attendance.attendance_ratio).toFixed(1)}%` : '-'}
                  </p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Score: {formatScorePercentage(scorePercentage)}
                  </p>
                  <p className="mt-1 text-xs text-muted-foreground">Grade: {mappedGrade}</p>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Sheet open={isEnrollDrawerOpen} onOpenChange={setIsEnrollDrawerOpen}>
        <SheetContent className="w-full max-w-full sm:max-w-xl p-0" showClose>
          <SheetHeader className="border-b p-4">
            <div className="flex flex-col gap-3 pr-10">
              <div>
                <SheetTitle>Enroll user</SheetTitle>
                <SheetDescription>
                  Users from board: <span className="font-medium text-foreground">{boardName || '-'}</span>
                </SheetDescription>
              </div>
              <div className="relative w-full">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={drawerSearchText}
                  onChange={(e) => setDrawerSearchText(e.target.value)}
                  placeholder="Search by name"
                  className="h-10 w-full pl-9"
                />
              </div>
            </div>
          </SheetHeader>
          <div className="max-h-[calc(100vh-160px)] overflow-y-auto">
            <div className="border-b p-4">
              <label className="flex items-center gap-2 text-sm font-medium">
                <input
                  type="checkbox"
                  checked={allVisibleCandidatesSelected}
                  onChange={(e) => handleSelectAllCandidatesChange(e.target.checked)}
                />
                Select all
              </label>
            </div>
            <div className="divide-y">
              {isBoardStudentsLoading ? (
                <div className="p-4 text-sm text-muted-foreground">Loading users...</div>
              ) : candidateStudents.length === 0 ? (
                <div className="p-4 text-sm text-muted-foreground">No users available to enroll.</div>
              ) : (
                candidateStudents.map((entry) => {
                  const isSelected = selectedCandidateIds.includes(entry.student.id);
                  return (
                    <label
                      key={entry.student.id}
                      className="flex cursor-pointer items-center gap-3 p-4 hover:bg-muted/30"
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setSelectedCandidateIds((prev) => {
                            if (checked) return [...prev, entry.student.id];
                            return prev.filter((id) => id !== entry.student.id);
                          });
                        }}
                      />
                      <Avatar className="h-10 w-10">
                        <AvatarImage
                          src={entry.student.photo_url || undefined}
                          alt={`${entry.student.first_name} ${entry.student.last_name}`}
                        />
                        <AvatarFallback>
                          {getInitials(entry.student.first_name, entry.student.last_name)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="min-w-0">
                        <p className="truncate font-medium">
                          {entry.student.first_name} {entry.student.last_name}
                        </p>
                        <p className="truncate text-sm text-muted-foreground">
                          {entry.student.email}
                        </p>
                      </div>
                    </label>
                  );
                })
              )}
            </div>
          </div>
          <SheetFooter className="border-t p-4">
            <Button
              className="ml-auto"
              onClick={() => void handleSaveEnrollments()}
              disabled={!selectedCandidateIds.length || isCreatingEnrollments}
            >
              Save
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      <AlertDialog
        open={enrollmentToDeleteId !== null}
        onOpenChange={(open) => {
          if (!open) setEnrollmentToDeleteId(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove enrolled user?</AlertDialogTitle>
            <AlertDialogDescription>
              {enrollmentToDelete
                ? `This will remove ${enrollmentToDelete.student.first_name} ${enrollmentToDelete.student.last_name} from this course.`
                : 'This will remove the selected user from this course.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive hover:bg-destructive/90"
              onClick={() => void handleDeleteEnrollment()}
              disabled={isDeletingEnrollment}
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={showUnmarkedConfirm} onOpenChange={setShowUnmarkedConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Unmarked learners will be absent</AlertDialogTitle>
            <AlertDialogDescription>
              Some learners are not marked. They will be saved as absent. Do you want to continue?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setShowUnmarkedConfirm(false);
                void executeSaveAttendance();
              }}
              disabled={isSavingAttendance}
            >
              Continue
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default UsersAttendanceContent;
